/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { Note } from '../types/types';
import type { DomController } from './DomController';
import type { DBService } from './DBService';
import type { SettingsService } from './SettingsService';
import type { UIService } from './UIService';
import { DriveService } from './DriveService';
import type { GeminiService } from './GeminiService';
import type { EditorService } from './EditorService';

/**
 * Manages the state and content of the current note, persisting to IndexedDB.
 */
export class NoteService {
  public currentNote: Note | null = null;
  public isDirty = false;
  private autosaveTimer: number | null = null;
  private activeAudioObjectURLs: Map<string, string> = new Map();
  private lastPersistedFingerprint: string | null = null;
  private statusManager: StatusManager;
  
  // Service registry getters
  private get dom() { return ServiceRegistry.get<DomController>('dom'); }
  private get db() { return ServiceRegistry.get<DBService>('dbService'); }
  private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
  private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
  private get geminiService() { return ServiceRegistry.get<GeminiService>('gemini'); }
  private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
  public driveService: any = null;

  constructor() {
    this.statusManager = new StatusManager(this.dom);

    // Listen for content re-render requests when returning from source view
    document.addEventListener('requestContentRerender', async (event: CustomEvent) => {
      if (this.currentNote && event.detail?.tabName) {
        await this.renderNoteContent(event.detail.tabName);
      }
    });
  }

  private buildSaveFingerprint(note: Note): string {
    return JSON.stringify({
      title: note.title,
      rawTranscription: note.rawTranscription,
      polishedNote: note.polishedNote,
      translatedNote: note.translatedNote,
      category: note.category,
      tags: note.tags,
      driveFileId: note.driveFileId,
      audioClips: (note.audioClips || []).map(clip => ({
        id: clip.id,
        name: clip.name,
        mimeType: clip.mimeType,
        size: clip.data?.size || 0,
      })),
    });
  }

  public injectDriveService(driveService: any) { this.driveService = driveService; }

  public async createNewNote(): Promise<void> {
    const now = Date.now();
    const newNote: Note = { 
      id: `note_${now}`, 
      rawTranscription: '', 
      polishedNote: '', 
      translatedNote: '', 
      timestamp: now,
      modifiedTimestamp: now, 
      driveFileId: null,
      title: 'Untitled Note',
      category: '',
      tags: [],
      audioClips: [],
    };
    this.currentNote = newNote;
    await this.db.saveNote(newNote);
    this.lastPersistedFingerprint = this.buildSaveFingerprint(newNote);
    await this.renderNoteUI();
    this.setDirty(false);
    this.statusManager.ready();
  }

  private isContentDifferent(noteA: Note, noteB: Note): boolean {
    return noteA.title !== noteB.title ||
           noteA.rawTranscription !== noteB.rawTranscription ||
           noteA.polishedNote !== noteB.polishedNote ||
           noteA.translatedNote !== noteB.translatedNote;
  }

  public async saveCurrentNote(markAsClean = true): Promise<void> {
    if (this.currentNote) {
      const title = this.currentNote.title?.trim() || '';
      const hasMeaningfulTitle = title.length > 0 && title !== 'Untitled Note';
      const hasContent = this.currentNote.rawTranscription.trim() ||
                         this.currentNote.polishedNote.trim() ||
                         this.currentNote.translatedNote.trim() ||
                         hasMeaningfulTitle ||
                         (this.currentNote.audioClips && this.currentNote.audioClips.length > 0);

      if (!hasContent) {
        // This is a blank note. Don't save it, and ensure it's not marked as dirty.
        this.setDirty(false);
        return;
      }

      const currentFingerprint = this.buildSaveFingerprint(this.currentNote);
      if (this.lastPersistedFingerprint === currentFingerprint) {
        if (markAsClean) {
          this.setDirty(false);
        }
        return;
      }

      if (markAsClean && !this.currentNote.driveFileId) { // Only save local history on explicit saves
        const latestHistory = await this.db.getLatestNoteHistory(this.currentNote.id);
        if (!latestHistory || this.isContentDifferent(this.currentNote, latestHistory.snapshot)) {
             await this.db.addNoteHistory(this.currentNote);
        }
      }
      this.currentNote.modifiedTimestamp = Date.now();
      await this.db.saveNote(this.currentNote);
      this.lastPersistedFingerprint = this.buildSaveFingerprint(this.currentNote);
      this.refreshNoteMetadataDisplay(); // Updates storage icon, categories, tags, and timestamp
      this.updateTabVisibility();
      this.uiService?.updateSideBySideButtonState();
      if (markAsClean) {
        this.setDirty(false);
      }
    }
  }

  public async loadNote(id: string): Promise<void> {
    const note = await this.db.getNote(id);
    if (note) {
      // Check if this is a metadata-only note that needs content loading
      if (note._metadataOnly && note.driveFileId && this.driveService) {
        try {
          this.statusManager.loading('Loading note content');
          // Load full content from Drive
          const content = await this.driveService.loadNoteContent(note.driveFileId);
          // Update the note with full content (preserve existing metadata)
          note.title = content.title?.trim() ? content.title : note.title;
          note.rawTranscription = content.rawTranscription || '';
          note.polishedNote = content.polishedNote || '';
          note.translatedNote = content.translatedNote || '';
          note.timestamp = content.timestamp;
          note._metadataOnly = false; // Mark as fully loaded

          // Parse the file description for metadata (category and tags)
          if (content.description) {
            ErrorHandler.log('loading content', `Loading content for note: ${note.title}`);
            ErrorHandler.log('loading content', `File description: "${content.description}"`);

            const fileMetadata = DriveService.parseDescriptionForMetadata(content.description);

            if (fileMetadata.category && !note.category) {
                note.category = fileMetadata.category;
                ErrorHandler.log('metadata update', `Updated category from Drive description: "${note.category}"`);
            } else if (fileMetadata.category) {
                ErrorHandler.log('metadata update', `Category already exists: "${note.category}", Drive has: "${fileMetadata.category}"`);
            }

            if (fileMetadata.tags && fileMetadata.tags.length > 0) {
                // Merge with existing tags, avoiding duplicates
                const existingTags = note.tags || [];
                const newTags = fileMetadata.tags.filter(tag => !existingTags.includes(tag));
                note.tags = [...existingTags, ...newTags];

                if (newTags.length > 0) {
                  ErrorHandler.log('metadata update', `Added new tags from Drive description: [${newTags.join(', ')}]`);
                  ErrorHandler.log('metadata update', `Complete tag list: [${note.tags.join(', ')}]`);
                } else {
                  ErrorHandler.log('metadata update', `No new tags to add. Current tags: [${existingTags.join(', ')}]`);
                }
            } else {
              ErrorHandler.log('metadata update', 'No tags found in Drive description');
            }
          } else {
            ErrorHandler.warn('metadata update', `No description available for note: ${note.title}`);
          }

          // Save the updated note locally
          await this.db.saveNote(note);
          this.statusManager.success('Note content loaded');
        } catch (error) {
          ErrorHandler.handle('loading note content', error, this.dom);
        }
      }

      this.currentNote = note;
      this.lastPersistedFingerprint = this.buildSaveFingerprint(note);
      await this.renderNoteUI();
      this.setDirty(false);
    }
  }

  public async loadMostRecentNote(): Promise<void> {
      const note = await this.db.getMostRecentNote();
      if (note) {
        this.currentNote = note;
        this.lastPersistedFingerprint = this.buildSaveFingerprint(note);
        await this.renderNoteUI();
      } else {
        await this.createNewNote();
      }
      this.setDirty(false);
  }
  
  public async renderNoteUI(): Promise<void> {
    if (!this.currentNote) return;

    this._cleanupAudioURLs();

    // Show loading indicator for metadata-only notes
    if (this.currentNote._metadataOnly) {
        this.dom.editorTitle.textContent = this.currentNote.title || 'Loading content...';
        this.dom.rawTranscription.textContent = 'Content will appear here once loaded from Google Drive...';
        this.dom.polishedNote.innerHTML = '<p><em>Note content is being loaded from Google Drive...</em></p>';
        this.dom.translatedNote.innerHTML = '<p><em>Translated content will appear here...</em></p>';
    }
    
    // Parse Markdown to HTML first. Handle empty strings gracefully.
    const polishedHtml = this.currentNote.polishedNote ? await parseMarkdown(this.currentNote.polishedNote) : '';
    const translatedHtml = this.currentNote.translatedNote ? await parseMarkdown(this.currentNote.translatedNote) : '';

    this.updatePlaceholder(this.dom.editorTitle, this.currentNote.title);
    this.updatePlaceholder(this.dom.rawTranscription, this.currentNote.rawTranscription);

    // updatePlaceholder will now handle setting the innerHTML with the *parsed* content
    this.updatePlaceholder(this.dom.polishedNote, polishedHtml, true);
    this.updatePlaceholder(this.dom.translatedNote, translatedHtml, true);

    this.renderAudioClips();

    // Refresh metadata display (includes storage icon, categories, tags, loading state, and timestamp)
    this.refreshNoteMetadataDisplay();

    this.updateTabVisibility();
    this.uiService?.updateSideBySideButtonState();

    this.dom.saveTranslatedOption.classList.add('hidden');
    this.dom.saveTranslatedToggle.checked = false;

    const polishedTab = document.querySelector('[data-tab="note"]') as HTMLButtonElement;
    if (polishedTab) this.dom.setActiveTab(polishedTab, true);
  }

  private _cleanupAudioURLs(): void {
    this.activeAudioObjectURLs.forEach(url => URL.revokeObjectURL(url));
    this.activeAudioObjectURLs.clear();
  }

  private renderAudioClips(): void {
    const container = this.dom.audioClipsContainer;
    container.innerHTML = '';

    if (this.currentNote?.audioClips && this.currentNote.audioClips.length > 0) {
      this.dom.audioClipsWrapper.classList.remove('hidden');
      this.currentNote.audioClips.forEach(clip => {
        const player = this.createAudioPlayer(clip);
        container.appendChild(player);
      });
    } else {
      this.dom.audioClipsWrapper.classList.add('hidden');
    }
  }
  
  private createAudioPlayer(clip: Note['audioClips'][0]): HTMLElement {
    const player = document.createElement('div');
    player.className = 'audio-player';

    const audio = document.createElement('audio');
    const objectURL = URL.createObjectURL(clip.data);
    this.activeAudioObjectURLs.set(clip.id, objectURL);
    audio.src = objectURL;

    const playButton = document.createElement('button');
    playButton.className = 'audio-player-button';
    playButton.innerHTML = `<i class="fas fa-play"></i>`;

    const nameDisplay = document.createElement('div');
    nameDisplay.className = 'audio-player-name';
    nameDisplay.textContent = clip.name;

    const progressContainer = document.createElement('div');
    progressContainer.className = 'audio-player-progress-container';
    const progressBar = document.createElement('div');
    progressBar.className = 'audio-player-progress-bar';
    progressContainer.appendChild(progressBar);
    
    const timeDisplay = document.createElement('div');
    timeDisplay.className = 'audio-player-time';
    timeDisplay.textContent = '0:00 / 0:00';
    
    const deleteButton = document.createElement('button');
    deleteButton.className = 'audio-player-button audio-player-delete-button';
    deleteButton.innerHTML = `<i class="fas fa-trash-alt"></i>`;
    deleteButton.title = 'Delete audio clip';

    const transcribeButton = document.createElement('button');
    transcribeButton.className = 'audio-player-button audio-player-transcribe-button';
    transcribeButton.innerHTML = `<i class="fas fa-microphone-alt"></i>`;
    transcribeButton.title = 'Transcribe audio clip';

    const formatTime = (seconds: number) => {
      const min = Math.floor(seconds / 60);
      const sec = Math.floor(seconds % 60);
      return `${min}:${String(sec).padStart(2, '0')}`;
    };

    audio.addEventListener('loadedmetadata', () => {
      timeDisplay.textContent = `0:00 / ${formatTime(audio.duration)}`;
    });

    audio.addEventListener('timeupdate', () => {
      progressBar.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
      timeDisplay.textContent = `${formatTime(audio.currentTime)} / ${formatTime(audio.duration)}`;
    });

    audio.addEventListener('play', () => playButton.innerHTML = `<i class="fas fa-pause"></i>`);
    audio.addEventListener('pause', () => playButton.innerHTML = `<i class="fas fa-play"></i>`);
    audio.addEventListener('ended', () => playButton.innerHTML = `<i class="fas fa-play"></i>`);
    
    playButton.addEventListener('click', () => audio.paused ? audio.play() : audio.pause());

    progressContainer.addEventListener('click', (e) => {
      const rect = progressContainer.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audio.currentTime = percent * audio.duration;
    });

    deleteButton.addEventListener('click', () => {
        if (this.currentNote?.audioClips) {
            // Stop the audio and destroy the player instance
            if (!audio.paused) {
                audio.pause();
            }
            audio.src = '';
            audio.load();
            
            // Remove the audio clip from the note
            this.currentNote.audioClips = this.currentNote.audioClips.filter(c => c.id !== clip.id);
            
            // Remove the player element from DOM
            player.remove();
            
            // Re-render audio clips and save
            this.renderAudioClips();
            this.saveCurrentNote(false);
        }
    });

    transcribeButton.addEventListener('click', async () => {
        if (!this.geminiService || !this.currentNote) return;
        
        // Disable button during transcription
        transcribeButton.disabled = true;
        transcribeButton.innerHTML = `<i class="fas fa-spinner fa-spin"></i>`;
        transcribeButton.title = 'Transcribing...';
        
        try {
            this.statusManager.loading('Converting audio to text');
            
            // Convert blob to base64
            const reader = new FileReader();
            const base64Audio = await new Promise<string>((resolve, reject) => {
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(clip.data);
            });
            
            // Get transcription
            const { text: transcriptionText } = await this.geminiService.getTranscription(
                base64Audio, 
                clip.mimeType, 
                this.settingsService.getVoiceTranscriptionModel()
            );
            
            if (transcriptionText && this.currentNote) {
                // Get the active editor
                const activeEditor = this.editorService.getActiveEditor();
                if (!activeEditor) {
                    this.statusManager.error('No active editor found');
                    return;
                }
                
                // Format the transcribed text with separator
                const formattedText = `\n\n--- Transcribed from "${clip.name}" ---\n${transcriptionText}`;
                
                // Insert into the active editor
                if (activeEditor.id === 'polishedNote' || activeEditor.id === 'translatedNote') {
                    // For markdown editors, insert as markdown
                    const markdownText = formattedText;
                    this.editorService.insertContent(markdownText);
                } else {
                    // For raw transcription, insert as plain text
                    this.editorService.insertContent(formattedText);
                }
                
                // Update note model and save
                this.editorService.syncActiveContentToModel();
                this.setDirty(true);
                await this.saveCurrentNote();
                
                this.statusManager.success('Transcription');
            } else {
                this.statusManager.error('Transcription failed or returned empty');
            }
        } catch (error) {
            ErrorHandler.handle('transcribing audio clip', error, this.dom);
        } finally {
            // Restore button
            transcribeButton.disabled = false;
            transcribeButton.innerHTML = `<i class="fas fa-microphone-alt"></i>`;
            transcribeButton.title = 'Transcribe audio clip';
        }
    });

    player.appendChild(playButton);
    player.appendChild(nameDisplay);
    player.appendChild(progressContainer);
    player.appendChild(timeDisplay);
    player.appendChild(transcribeButton);
    player.appendChild(deleteButton);
    return player;
  }

  private updatePlaceholder(el: HTMLElement, content: string, isHtml = false) {
    const placeholder = el.getAttribute('placeholder') || '';
    if (content.trim() === '') {
        el.innerHTML = placeholder; // Use innerHTML to reset content
        el.classList.add('placeholder-active');
    } else {
        if (isHtml) el.innerHTML = content;
        else el.textContent = content;
        el.classList.remove('placeholder-active');
    }
  }
  
  public syncUItoModel(isAutosave = false): void {
    if (!this.currentNote) return;

    const titleEl = this.dom.editorTitle;
    const uiTitle = titleEl.textContent?.trim() || '';
    const isUiTitleEmpty = titleEl.classList.contains('placeholder-active') || !uiTitle || uiTitle === 'Untitled Note';

    if (!isUiTitleEmpty) {
        this.currentNote.title = uiTitle;
    } else if (!this.currentNote.title?.trim()) {
        this.currentNote.title = 'Untitled Note';
    } else if (!isAutosave && this.currentNote.title !== 'Untitled Note') {
        this.currentNote.title = this.currentNote.title.trim();
    }

    this.currentNote.rawTranscription = this.dom.rawTranscription.classList.contains('placeholder-active') ? '' : this.dom.rawTranscription.textContent || '';
  }
  
  public updateTitleFromContent(polishedText: string): void {
      const lines = polishedText.split('\n').map(l => l.trim());
      let titleSet = false;
      for (const line of lines) {
          if (line.startsWith('#')) {
              const title = line.replace(/^#+\s+/, '').trim();
              if (title) {
                  this.dom.editorTitle.textContent = title;
                  this.dom.editorTitle.classList.remove('placeholder-active');
                  titleSet = true;
                  break;
              }
          }
      }
      if (!titleSet) {
          for (const line of lines) {
              if (line.length > 0) {
                  let potentialTitle = line.replace(/^[\*_\`#\->\s\[\]\(.\d)]+/, '').replace(/[\*_\`#]+$/, '').trim();
                  if (potentialTitle.length > 3) {
                      this.dom.editorTitle.textContent = potentialTitle.substring(0, 60) + (potentialTitle.length > 60 ? '...' : '');
                      this.dom.editorTitle.classList.remove('placeholder-active');
                      break;
                  }
              }
          }
      }
      this.syncUItoModel(true);
      this.setDirty(true);
      this.saveCurrentNote(false); // Save but keep dirty
  }

  public startAutosave(): void {
    this.stopAutosave(); // Ensure no multiple timers running
    if (this.settingsService.isAutosaveEnabled) {
      this.autosaveTimer = window.setInterval(async () => {
        this.syncUItoModel(true);
        await this.saveCurrentNote();
      }, this.settingsService.autosaveFrequency);
    }
  }

  public stopAutosave(): void {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
  }

  public restartAutosaveTimer(): void {
    this.stopAutosave();
    this.startAutosave();
  }

  public setDirty(dirty: boolean): void {
    if (this.isDirty === dirty) return; // No change
    this.isDirty = dirty;
    this.dom.saveNoteButton.disabled = !dirty;
    this.dom.saveNoteButton.classList.toggle('active', dirty);
  }
  
  public updateTabVisibility(): void {
    if (!this.currentNote) return;

    this.dom.rawTabButton.classList.toggle('hidden', !this.currentNote.rawTranscription.trim());
    this.dom.translatedTabButton.classList.toggle('hidden', !this.currentNote.translatedNote.trim());

    // If the active tab just got hidden, switch to the polished tab.
    const activeTab = document.querySelector('.tab-button.active') as HTMLButtonElement | null;
    if (activeTab && activeTab.classList.contains('hidden')) {
        const polishedTab = document.querySelector('[data-tab="note"]') as HTMLButtonElement;
        if (polishedTab) {
            this.dom.setActiveTab(polishedTab, true);
        }
    }
  }

  public updateStorageStatusIcon(note: Note | null): void {
    if (!note) return;
    const icon = this.dom.storageStatusIcon.querySelector('i')!;

    if (note._metadataOnly) {
        icon.className = 'fas fa-cloud-download-alt';
        this.dom.storageStatusIcon.title = 'Loading content from Google Drive...';
    } else if (note.driveFileId) {
        icon.className = 'fab fa-google-drive';
        this.dom.storageStatusIcon.title = `Note synced with Google Drive. Last modified: ${new Date(note.modifiedTimestamp).toLocaleString()}`;
    } else {
        icon.className = 'fas fa-database';
        this.dom.storageStatusIcon.title = `Note saved locally. Last modified: ${new Date(note.modifiedTimestamp).toLocaleString()}`;
    }
  }

  public refreshNoteMetadataDisplay(): void {
    if (!this.currentNote) return;

    // Clear current metadata display but preserve storage icon
    const storageIcon = this.dom.storageStatusIcon;
    this.dom.noteMetadataDisplay.innerHTML = '';
    
    // Re-add storage icon if it exists
    if (storageIcon) {
      this.dom.noteMetadataDisplay.appendChild(storageIcon);
    }

    // Update storage status icon
    this.updateStorageStatusIcon(this.currentNote);

    // Display category if exists
    if (this.currentNote.category) {
        const categoryEl = document.createElement('span');
        categoryEl.className = 'note-header-category';
        categoryEl.textContent = this.currentNote.category;
        this.dom.noteMetadataDisplay.appendChild(categoryEl);
    }

    // Display tags if they exist
    if (this.currentNote.tags && this.currentNote.tags.length > 0) {
        this.currentNote.tags.forEach(tag => {
            const tagEl = document.createElement('span');
            tagEl.className = 'note-header-tag';
            tagEl.textContent = tag;
            this.dom.noteMetadataDisplay.appendChild(tagEl);
        });
    }

    // Show loading indicator for metadata-only notes
    if (this.currentNote._metadataOnly) {
        const loadingEl = document.createElement('span');
        loadingEl.className = 'note-header-loading';
        loadingEl.textContent = 'Loading content...';
        loadingEl.style.opacity = '0.7';
        loadingEl.style.fontStyle = 'italic';
        this.dom.noteMetadataDisplay.appendChild(loadingEl);
    }

    // Add last modified timestamp as a subtle indicator
    const timestampEl = document.createElement('span');
    timestampEl.className = 'note-header-timestamp';
    timestampEl.textContent = `Modified: ${new Date(this.currentNote.modifiedTimestamp).toLocaleString()}`;
    timestampEl.style.opacity = '0.6';
    timestampEl.style.fontSize = '11px';
    timestampEl.style.marginLeft = '8px';
    this.dom.noteMetadataDisplay.appendChild(timestampEl);
  }

  private async renderNoteContent(tabName: string): Promise<void> {
    if (!this.currentNote) return;

    let content = '';
    let isHtml = false;

    switch (tabName) {
      case 'raw':
        content = this.currentNote.rawTranscription || '';
        isHtml = false; // Raw content is plain text
        break;
      case 'translated':
        content = this.currentNote.translatedNote || '';
        isHtml = true; // Translated content should be rendered as markdown
        break;
      case 'note':
      default:
        content = this.currentNote.polishedNote || '';
        isHtml = true; // Polished content should be rendered as markdown
        break;
    }

    // Find the active content element for this tab
    const contentElement = document.querySelector(`[data-content="${tabName}"]`) as HTMLElement;
    if (!contentElement) return;

    if (isHtml && content) {
      // Parse markdown to HTML
      contentElement.innerHTML = await parseMarkdown(content);
    } else {
      // Set as plain text
      contentElement.textContent = content;
    }
  }

  public async clearAllLocalData(): Promise<void> {
    try {
      // Clear all data from local database (notes, history, settings, token usage)
      await this.db.clearAllData();

      // Clear current note
      this.currentNote = null;
      this.isDirty = false;

      // Clear any audio URLs
      this._cleanupAudioURLs();

      // Clear UI
      this.dom.editorTitle.textContent = 'Untitled Note';
      this.dom.rawTranscription.textContent = '';
      this.dom.polishedNote.innerHTML = '';
      this.dom.translatedNote.innerHTML = '';
      this.dom.audioClipsContainer.innerHTML = '';
      this.dom.audioClipsWrapper.classList.add('hidden');

      // Clear current note reference (don't auto-create new one)
      this.currentNote = null;

      this.statusManager.custom('All local data cleared');
      ErrorHandler.log('data cleanup', 'All local data cleared');
    } catch (error) {
      ErrorHandler.handle('clearing local data', error, this.dom);
    }
  }
}